import React, { useEffect, useState } from "react";
import { createScenario, getScenario, updateScenario } from "../../api";
import ScenarioPromptEditor from "../../components/ScenarioPromptEditor";
import { SCENARIO_PROMPT_VARIABLES } from "../../constants/scenarioPromptVariables";

const CARD = "rounded-2xl border border-[#1f2638] bg-[#0e1320]/50 p-5";
const LBL = "text-xs text-slate-400";
const H2 = "text-sm font-semibold tracking-wider text-slate-200";
const INP =
    "w-full rounded-xl border border-[#1f2638] bg-[#0f1523] px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500";

const BTN_GHOST =
    "inline-flex items-center justify-center rounded-xl border border-transparent bg-transparent px-3 py-2 text-sm font-semibold hover:bg-[#0e1320] disabled:opacity-50 disabled:cursor-not-allowed";

const BTN_PRIMARY =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-violet-600/40 bg-violet-600/20 px-4 py-2 text-sm font-semibold hover:bg-violet-600/30 disabled:opacity-60 disabled:cursor-not-allowed";

export default function ScenarioForm({ scenarioId, onBack, onSaved }) {
    const isEdit = !!scenarioId;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const [name, setName] = useState("");
    const [prompt, setPrompt] = useState("");
    const [firstMessage, setFirstMessage] = useState("");

    useEffect(() => {
        let alive = true;

        const run = async () => {
            if (!isEdit) return;
            try {
                setErr("");
                setLoading(true);
                const s = await getScenario(scenarioId);
                if (!alive) return;

                setName(s?.name || "");
                setPrompt(s?.prompt || "");
                setFirstMessage(s?.first_message ?? s?.firstMessage ?? "");
            } catch (e) {
                if (!alive) return;
                setErr(e?.message || "Грешка при зареждане");
            } finally {
                if (alive) setLoading(false);
            }
        };

        run();
        return () => {
            alive = false;
        };
    }, [isEdit, scenarioId]);

    const validate = () => {
        const n = (name || "").trim();
        const p = (prompt || "").trim();
        const fm = (firstMessage || "").trim();

        if (!n) return "Име е задължителено.";
        if (!p) return "Промпт е задължителен.";
        if (!fm) return "Съобщение след приемане на обаждането е задължително.";
        return "";
    };

    const doSave = async () => {
        const v = validate();
        if (v) {
            setErr(v);
            return;
        }

        const payload = {
            name: name.trim(),
            prompt: prompt,
            first_message: firstMessage,
        };

        try {
            setSaving(true);
            setErr("");

            const saved = isEdit
                ? await updateScenario(scenarioId, payload)
                : await createScenario(payload);

            onSaved?.(saved);
        } catch (e) {
            setErr(e?.message || "Грешка при запис");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-xl font-bold">{isEdit ? "Промяна на сценарий" : "Нов сценарий"}</h1>
                <div className="flex-1" />

                <button className={BTN_PRIMARY} onClick={doSave} disabled={saving || loading}>
                    {saving ? "Запис..." : "Запази"}
                </button>

                {typeof onBack === "function" && (
                    <button className={BTN_GHOST} onClick={onBack} disabled={saving}>
                        Назад
                    </button>
                )}
            </div>

            {err && (
                <div className="rounded-2xl border border-rose-600/30 bg-rose-600/10 p-4 text-sm text-rose-200">
                    {err}
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-6">
                <section className={CARD}>
                    <div className={H2}>Основна информация</div>

                    {loading ? (
                        <div className="mt-4 text-sm text-slate-300">Зареждане...</div>
                    ) : (
                        <div className="mt-4 space-y-4 text-sm">
                            <div>
                                <div className={LBL}>Име</div>
                                <input
                                    className={INP}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="пример: Редовен платец"
                                    disabled={saving || loading}
                                />
                            </div>
                            <div>
                                <div className={LBL}>Съобщение след приемане на обаждането</div>
                                <textarea
                                    className={[INP, "min-h-[112px] resize-y"].join(" ")}
                                    value={firstMessage}
                                    onChange={(e) => setFirstMessage(e.target.value)}
                                    disabled={saving || loading}
                                />
                            </div>
                        </div>
                    )}
                </section>

                <section className={CARD}>
                    <div className={H2}>Промпт</div>

                    <div className="mt-4">
                        <ScenarioPromptEditor
                            value={prompt}
                            onChange={setPrompt}
                            placeholder="..."
                            variables={SCENARIO_PROMPT_VARIABLES}
                            disabled={saving || loading}
                        />
                    </div>
                </section>
            </div>
        </div>
    );
}
